import Swiper from './swiper-bundle.esm.browser.min.js';
import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';

if (!customElements.get('lookbook-carousel')) {
  customElements.define(
    'lookbook-carousel',
    class LookbookCarousel extends HTMLElement {
      constructor() {
        super();
        this.swiper = null;
        this.lightbox = null;
      }

      connectedCallback() {
        this.readConfiguration();
        this.initSwiper();

        if (this.dataset.lightbox === 'true') {
          this.initLightbox();
        }
      }

      disconnectedCallback() {
        if (this.swiper) {
          this.swiper.destroy();
          this.swiper = null;
        }
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
      }

      readConfiguration() {
        const defaultConfig = {
          slidesPerViewMobile: 2,
          slidesPerViewTablet: 2,
          slidesPerViewDesktop: 3,
          spaceBetween: 16,
          loop: true,
          autoplay: false,
          navigation: true,
          pagination: true,
        };

        const customConfig = this.dataset.swiperConfig ? JSON.parse(this.dataset.swiperConfig) : {};

        this.configuration = {
          ...defaultConfig,
          ...customConfig,
        };
      }

      initSwiper() {
        const container = this.querySelector('[data-swiper]');
        if (!container) return;

        container.classList.add('swiper', 'wt-slider__container');
        container.querySelector('[data-swiper-container]')?.classList.add('swiper-wrapper', 'wt-slider__wrapper');
        container.querySelectorAll('[data-swiper-slide]').forEach((slide) => {
          slide.classList.add('swiper-slide', 'wt-slider__slide');
        });

        const swiperConfig = {
          slidesPerView: this.configuration.slidesPerViewMobile,
          spaceBetween: this.configuration.spaceBetween,
          loop: this.configuration.loop,
          loopAdditionalSlides: 2,
          threshold: 5,
          grabCursor: true,
          preventClicks: false,
          preventClicksPropagation: false,
          watchSlidesProgress: true,
          autoplay: this.configuration.autoplay ? {
            delay: 5000,
            disableOnInteraction: false,
          } : false,
          navigation: this.configuration.navigation ? {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          } : false,
          pagination: this.configuration.pagination ? {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
          } : false,
          breakpoints: {
            750: {
              slidesPerView: this.configuration.slidesPerViewTablet,
              spaceBetween: this.configuration.spaceBetween,
            },
            1200: {
              slidesPerView: this.configuration.slidesPerViewDesktop,
              spaceBetween: this.configuration.spaceBetween * 1.5,
            },
          },
          on: {
            afterInit: (swiper) => {
              container.classList.remove('loading');
              this.handleTabindex(swiper);
            },
            slideChange: (swiper) => {
              this.handleTabindex(swiper);
            },
          },
        };

        this.swiper = new Swiper(container, swiperConfig);
      }

      handleTabindex(swiper) {
        const focusableSelectors = 'a, button';
        const totalSlides = swiper.slides.length;
        const slidesPerView = swiper.params.slidesPerView;

        if (totalSlides > slidesPerView) {
          swiper.slides.forEach((slide) => {
            const slideRect = slide.getBoundingClientRect();
            const swiperRect = this.getBoundingClientRect();

            const isFullyVisible =
              slideRect.left >= swiperRect.left - 1 &&
              slideRect.right <= swiperRect.right + 1 &&
              slideRect.top >= swiperRect.top - 1 &&
              slideRect.bottom <= swiperRect.bottom + 1;

            slide.querySelectorAll(focusableSelectors).forEach((el) => {
              el.setAttribute('tabindex', isFullyVisible ? '0' : '-1');
            });
          });
        } else {
          swiper.slides.forEach((slide) => {
            slide.querySelectorAll(focusableSelectors).forEach((el) => {
              el.setAttribute('tabindex', '0');
            });
          });
        }
      }

      initLightbox() {
        this.lightbox = new PhotoSwipeLightbox({
          gallery: this,
          children: 'a.lookbook-lightbox-trigger',
          pswpModule: () => import('./photoswipe.esm.min.js'),
          bgOpacity: 1,
          padding: { top: 40, bottom: 40, left: 20, right: 20 },
          loop: true,
          wheelToZoom: false,
          initialZoomLevel: 'fit',
          secondaryZoomLevel: 1,
          maxZoomLevel: 1,
          pinchToClose: false,
          doubleTapAction: false,
          zoom: false,
          showHideAnimationType: 'zoom',
          getThumbBoundsFn: (index) => {
            const thumbnail = this.querySelectorAll('a.lookbook-lightbox-trigger')[index];
            if (!thumbnail) return null;

            const img = thumbnail.querySelector('img');
            if (!img) return null;

            const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
            const rect = img.getBoundingClientRect();

            return {
              x: rect.left,
              y: rect.top + pageYScroll,
              w: rect.width
            };
          }
        });

        this.lightbox.on('uiRegister', () => {
          this.lightbox.pswp.ui.registerElement({
            name: 'custom-caption',
            order: 9,
            isButton: false,
            appendTo: 'root',
            html: '',
            onInit: (el, pswp) => {
              pswp.on('change', () => {
                const currSlideElement = pswp.currSlide?.data?.element;
                if (!currSlideElement) {
                  el.innerHTML = '';
                  return;
                }
                let captionHTML = '';
                const hiddenCaption = currSlideElement.querySelector('.hidden-caption-content');
                if (hiddenCaption) {
                  captionHTML = hiddenCaption.innerHTML;
                } else {
                  const img = currSlideElement.querySelector('img');
                  if (img && img.alt) {
                    captionHTML = img.alt;
                  }
                }
                el.innerHTML = captionHTML || '';
              });
            }
          });
        });

        this.lightbox.addFilter('isContentZoomable', () => false);

        this.lightbox.init();

        const triggers = this.querySelectorAll('a.lookbook-lightbox-trigger');
        triggers.forEach((trigger, index) => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.lightbox.loadAndOpen(index, {
              gallery: this,
            });
          });
        });
      }
    }
  );
}
